// Simple in-memory cache with TTL (Time To Live)

interface CacheEntry<T> {
  data: T
  timestamp: number
  ttl: number
}

class Cache {
  private store: Map<string, CacheEntry<any>> = new Map()
  private maxSize: number = 100

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize
  }

  set<T>(key: string, data: T, ttlSeconds: number = 300): void {
    if (this.store.size >= this.maxSize) {
      this.cleanExpired()
    }

    this.store.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttlSeconds * 1000
    })
  }

  get<T>(key: string): T | null {
    const entry = this.store.get(key)
    if (!entry) return null

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.store.delete(key)
      return null
    }

    return entry.data as T
  }

  has(key: string): boolean {
    return this.get(key) !== null
  }

  delete(key: string): void {
    this.store.delete(key)
  }

  clear(): void {
    this.store.clear()
  }

  cleanExpired(): void {
    const now = Date.now()
    for (const [key, entry] of this.store.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.store.delete(key)
      }
    }
  }

  getStats() {
    return {
      size: this.store.size,
      maxSize: this.maxSize,
      keys: Array.from(this.store.keys())
    }
  }
}

// Export singleton instances
export const videoCache = new Cache(200)
export const commentCache = new Cache(100)
export const userCache = new Cache(50)
export const genreCache = new Cache(20)
export const searchCache = new Cache(30)